/**
 * Play video when in view
 *
 * Plays a video while it's scrolled into view and pauses it when it
 * scrolls out. Supports two kinds of elements with a `play-in-view` class:
 * - Vimeo iframe embeds (API script is loaded on demand)
 * - Native <video> element
 * Note that inline `allow="autoplay"` (Vimeo) or `playsinline="true"` attributes 
 * are required for mobile.
 */

const VIMEO_API_SRC = 'https://player.vimeo.com/api/player.js';
let vimeoApiPromise = null;

function loadVimeoPlayerAPI() {
  if (vimeoApiPromise) return vimeoApiPromise;

  vimeoApiPromise = new Promise((resolve, reject) => {
    if (typeof Vimeo !== 'undefined' && Vimeo.Player) {
      resolve();
      return;
    }

    const existing = document.querySelector(`script[src="${VIMEO_API_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = VIMEO_API_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return vimeoApiPromise;
}

function observePlayback(el, player, isVimeo) {
  const observerOptions = {
    root: null,       // Defaults to the browser viewport
    threshold: 0.5    // Trigger when 50% of the video is visible on screen
  };

  // Tracks whether the current play/pause call was programmatic 
  // (initiated by this script) or user-initiated, 
  // so a manual pause can suppress future auto-play.
  let isProgrammatic = false;
  let programmaticTimer = null;
  let userPaused = false;

  const markProgrammatic = () => {
    isProgrammatic = true;
    clearTimeout(programmaticTimer);
    // Vimeo's play/pause events arrive async via postMessage, so give them
    // a moment to land before treating further events as user-initiated.
    programmaticTimer = setTimeout(() => {
      isProgrammatic = false;
    }, 500);
  };

  const play = () => {
    if (userPaused) return;

    markProgrammatic();
    const result = player.play();
    if (result && typeof result.catch === 'function') {
      result.catch((error) => {
        console.log('[play-in-view] Autoplay was blocked or interrupted:', error);
      });
    }
    // console.log('[play-in-view] Playing video:', el);
  };

  const pause = () => {
    markProgrammatic();
    player.pause();
    // console.log('[play-in-view] Pausing video:', el);
  };

  const handlePlayEvent = () => {
    if (isProgrammatic) return;
    userPaused = false;
  };

  const handlePauseEvent = () => {
    if (isProgrammatic) return;
    userPaused = true;
    // console.log('[play-in-view] User paused video, suspending autoplay:', el);
  };

  if (isVimeo) {
    player.on('play', handlePlayEvent);
    player.on('pause', handlePauseEvent);
  } else {
    player.addEventListener('play', handlePlayEvent);
    player.addEventListener('pause', handlePauseEvent);
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        play();
      } else {
        pause();
      }
    });
  }, observerOptions);

  observer.observe(el);
}

/**
 * Initializes play-in-view behaviour for all elements matching selector
 * @param {string} selector - CSS selector for elements to observe (default: '.play-in-view')
 */
export function initPlayInView(selector = '.play-in-view') {
  const elements = document.querySelectorAll(selector);
  if (!elements.length) return;

  const vimeoEls = [];
  const videoEls = [];

  elements.forEach((el) => {
    if (el.tagName === 'IFRAME') {
      vimeoEls.push(el);
    } else {
      videoEls.push(el);
    }
  });

  videoEls.forEach((el) => observePlayback(el, el, false));

  if (vimeoEls.length) {
    loadVimeoPlayerAPI()
      .then(() => {
        vimeoEls.forEach((el) => observePlayback(el, new Vimeo.Player(el), true));
      })
      .catch((error) => {
        console.warn('[play-in-view] Failed to load Vimeo Player API:', error);
      });
  }
}

/**
 * Auto-initializes play-in-view when DOM is ready
 */
export function autoInitPlayInView() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPlayInView());
  } else {
    initPlayInView();
  }
}

autoInitPlayInView();

// Default export so other modules can use
export default {
  initPlayInView,
  autoInitPlayInView
};
